/**
 * `validateHandbackUrl` — boundary validation for the app-supplied
 * `handbackUrl` per ADR-027 §1 step 1.
 *
 * The orchestrator treats initiation parameters as untrusted (the
 * registry surface is the source of truth for verified identity).
 * The hand-back URL is the structural seam at the END of the flow
 * — the orchestrator emits it at `granted` / `denied` for the view
 * layer to redirect to. An unvalidated hand-back URL is an
 * exfiltration / open-redirect vector:
 *
 *   - `javascript:` / `data:` / `vbscript:` schemes execute in the
 *     PHG origin if the view layer follows the URL via
 *     `window.location` or an `<a href>` click — leaking the
 *     artifact reference (or worse) to attacker code running in
 *     the PHG document origin.
 *   - Embedded userinfo (`https://user:pass@host/...`) is
 *     deprecated by browsers and a known phishing vector.
 *   - Insecure `http:` over the wire would leak the artifact
 *     reference to passive observers.
 *
 * **Allowed scheme:** `https:` only at v0. Custom-scheme deep-link
 * support (e.g. `myapp://`) is substrate-state-event-paced for
 * registry-allowlist composition — when `RegisteredAppMetadata`
 * gains a `registeredHandbackUrls` field (DEV2 registry contract
 * scope), this validator extends to allow registered custom
 * schemes per the verified app's registration. Until then,
 * apps integrate via `https:` redirect; native shells wrap the
 * https return.
 *
 * **Composes with:** consent UI seam doctrinal anchor per
 * `feedback_phg_client_consent_ui_seam` (third-party apps NEVER
 * render PHG consent UI in their own surface; the hand-back URL
 * is the architectural boundary at flow terminal).
 */
const ALLOWED_HANDBACK_SCHEMES = new Set(['https:']);
export function validateHandbackUrl(raw, options) {
    let parsed;
    try {
        parsed = new URL(raw);
    }
    catch {
        return {
            ok: false,
            code: 'invalid-format',
            message: 'hand-back URL is not a valid absolute URL',
        };
    }
    if (!ALLOWED_HANDBACK_SCHEMES.has(parsed.protocol)) {
        return {
            ok: false,
            code: 'invalid-scheme',
            message: `hand-back URL scheme "${parsed.protocol}" is not allowed; only https: is permitted at v0`,
        };
    }
    if (parsed.username !== '' || parsed.password !== '') {
        return {
            ok: false,
            code: 'embedded-credentials',
            message: 'hand-back URL must not contain embedded user/password credentials',
        };
    }
    // M5/NL2 — registered-allowlist enforcement per CTO security re-scan
    // delta. Substrate-state-event-paced on DEV2 substrate-types
    // `RegisteredAppMetadata.registeredHandbackUrls` extension; when
    // options.registeredHandbackUrls is undefined (v0 default), allowlist
    // check is skipped (pre-extension behavior canonical). When defined,
    // exact-origin match canonical against the registered allowlist.
    if (options?.registeredHandbackUrls !== undefined) {
        const allowedOrigins = new Set(options.registeredHandbackUrls.map((registered) => safeOrigin(registered)));
        if (!allowedOrigins.has(parsed.origin)) {
            return {
                ok: false,
                code: 'not-in-registered-allowlist',
                message: `hand-back URL origin "${parsed.origin}" is not in the registered allowlist for this app`,
            };
        }
    }
    return { ok: true };
}
/**
 * Safe `URL.origin` extraction for allowlist comparison. Malformed
 * registered URLs (theoretically blocked at registration boundary;
 * defense-in-depth here) yield a sentinel origin that cannot match
 * any valid parsed URL — preserving fail-closed posture at the
 * validator.
 */
function safeOrigin(raw) {
    try {
        return new URL(raw).origin;
    }
    catch {
        return '__INVALID_REGISTERED_URL__';
    }
}
//# sourceMappingURL=validate-handback-url.js.map